import React, { useState } from 'react';
import { Caravan, Store, UserCheck } from 'lucide-react';
import { 
  TruckIcon, 
  UserGroupIcon, 
  UserIcon, 
  BuildingOfficeIcon, 
  GlobeAltIcon,
  CogIcon,
  CubeIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Package } from 'lucide-react';
import VehicleManagement from '../components/administration/VehicleManagement';
import TrailerManagement from '../components/administration/TrailerManagement';
import CarrierManagement from '../components/administration/CarrierManagement';
import DriverManagement from '../components/administration/DriverManagement';
import ClientManagement from '../components/administration/ClientManagement';
import SupplierManagement from '../components/administration/SupplierManagement';
import ExporterManagement from '../components/administration/ExporterManagement';
import ProductManagement from '../components/administration/ProductManagement';
import PackagingManagement from '../components/administration/PackagingManagement';
import StoreManagement from '../components/administration/StoreManagement';
import ConvoyeurManagement from '../components/administration/ConvoyeurManagement';

const AdministrationPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const tabs = [
    {
      name: 'Véhicules',
      icon: TruckIcon,
      component: VehicleManagement,
      description: 'Gestion des camions et véhicules'
    },
    {
      name: 'Remorques',
      icon: Caravan,
      component: TrailerManagement,
      description: 'Gestion des remorques'
    },
    {
      name: 'Transporteurs',
      icon: UserGroupIcon,
      component: CarrierManagement,
      description: 'Gestion des entreprises de transport'
    },
    {
      name: 'Chauffeurs',
      icon: UserIcon,
      component: DriverManagement,
      description: 'Gestion des chauffeurs'
    },
    {
      name: 'Convoyeurs',
      icon: UserCheck,
      component: ConvoyeurManagement,
      description: 'Gestion des convoyeurs et accompagnateurs'
    },
            {
          name: 'Magasins',
          icon: Store,
          component: StoreManagement,
          description: 'Gestion des magasins et dépôts'
        },
    {
      name: 'Clients',
      icon: BuildingOfficeIcon,
      component: ClientManagement,
      description: 'Gestion des clients'
    },
    {
      name: 'Fournisseurs',
      icon: BuildingOfficeIcon,
      component: SupplierManagement,
      description: 'Gestion des fournisseurs'
    },
    {
      name: 'Exportateurs',
      icon: GlobeAltIcon,
      component: ExporterManagement,
      description: 'Gestion des exportateurs'
    },
    {
      name: 'Produits',
      icon: CubeIcon,
      component: ProductManagement,
      description: 'Gestion des produits et marchandises'
    },
    {
      name: 'Conditionnement',
      icon: Package,
      component: PackagingManagement,
      description: 'Gestion des types d\'emballages et conditionnements'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar - Desktop */}
        <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        }`}>
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto max-h-screen">
            {/* Header */}
            <div className="flex items-center flex-shrink-0 px-4 mb-8">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <CogIcon className="h-8 w-8 text-orange-600" />
                </div>
                {!isSidebarCollapsed && (
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">Administration</h1>
                    <p className="text-sm text-gray-600">Gestion du système</p>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation */}
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {tabs.map((tab, index) => (
                <button
                  key={tab.name}
                  onClick={() => setSelectedTab(index)}
                  className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    selectedTab === index
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                  title={isSidebarCollapsed ? tab.name : undefined}
                >
                  <tab.icon className={`h-5 w-5 ${
                    selectedTab === index ? 'text-orange-500' : 'text-gray-400 group-hover:text-orange-500'
                  } ${isSidebarCollapsed ? '' : 'mr-3'}`} />
                  {!isSidebarCollapsed && tab.name}
                </button>
              ))}
            </nav>
            
            {/* Bouton pour plier/déplier le sidebar */}
            <div className="px-2 mt-4">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className={`w-full flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  isSidebarCollapsed 
                    ? 'text-orange-600 hover:bg-orange-50 bg-orange-50' 
                    : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                }`}
                title={isSidebarCollapsed ? "Déplier le menu" : "Plier le menu"}
                aria-label={isSidebarCollapsed ? "Déplier le menu" : "Plier le menu"}
              >
                {isSidebarCollapsed ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu button */}
        <div className="lg:hidden fixed top-4 left-4 z-50">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="block h-6 w-6" />
            ) : (
              <Bars3Icon className="block h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>
              
              {/* Mobile Header */}
              <div className="flex items-center flex-shrink-0 px-4 py-4">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <CogIcon className="h-8 w-8 text-orange-600" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">Administration</h1>
                    <p className="text-sm text-gray-600">Gestion du système</p>
                  </div>
                </div>
              </div>

              {/* Mobile Navigation */}
              <div className="mt-5 flex-1 px-2 space-y-1">
                {tabs.map((tab, index) => (
                  <button
                    key={tab.name}
                    onClick={() => {
                      setSelectedTab(index);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                      selectedTab === index
                        ? 'bg-orange-100 text-orange-700'
                        : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                    }`}
                  >
                    <tab.icon className={`mr-3 h-5 w-5 ${
                      selectedTab === index ? 'text-orange-500' : 'text-gray-400 group-hover:text-orange-500'
                    }`} />
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}>
          <div className="flex-1 flex flex-col min-h-0">
            {/* Mobile Header */}
            <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-4 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <CogIcon className="h-8 w-8 text-orange-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Administration</h1>
                  <p className="text-gray-600">Gérez tous les paramètres et entités du système</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
              <div className="bg-white rounded-lg shadow-sm border p-6 h-full overflow-y-auto">
                <div className="mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {React.createElement(tabs[selectedTab].icon, { className: "h-6 w-6 text-orange-600" })}
                      <span className="text-xl font-semibold text-gray-900">{tabs[selectedTab].name}</span>
                    </div>
                    {isSidebarCollapsed && (
                      <button
                        onClick={() => setIsSidebarCollapsed(false)}
                        className="lg:hidden inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        Afficher le menu
                      </button>
                    )}
                  </div>
                  <p className="text-gray-600 mt-1">{tabs[selectedTab].description}</p>
                </div>
                
                {React.createElement(tabs[selectedTab].component)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdministrationPage;
